import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, userIsClient } from '@/store/auth';
import { usePageMeta } from '@/hooks/usePageMeta';

interface FieldErrors {
  email?: string[];
  password?: string[];
}

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [magicBusy, setMagicBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);

  // i18n_ — SEO: public sign-in page is indexable, so set a meaningful title
  // and meta description. Uses locale-aware strings with sensible fallbacks.
  usePageMeta({
    title: t('i18n_seo.login_title', { defaultValue: 'Sign in | AIAds' }),
    description: t('i18n_seo.login_description', {
      defaultValue:
        'Sign in to AIAds — Google Ads management platform with AI copilot for agencies.',
    }),
    url: 'https://aiads.trustluna.com/login',
    robots: 'index, follow',
  });

  async function handleSsoGoogle() {
    setSsoBusy(true);
    try {
      const resp = await api.get<{ url: string }>('/auth/sso/google/redirect');
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        toast.error('SSO nie jest skonfigurowane');
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 503) {
        toast.error('SSO nie jest skonfigurowane');
      } else {
        toast.error('Nie udało się rozpocząć logowania SSO');
      }
    } finally {
      setSsoBusy(false);
    }
  }

  async function handleMagicLink() {
    if (!email) {
      toast.error('Podaj adres email');
      return;
    }
    setMagicBusy(true);
    try {
      await api.post('/auth/magic-link/request', { email });
      toast.success('Sprawdź skrzynkę — wysłaliśmy link do logowania');
    } catch {
      toast.error('Nie udało się wysłać magic linka');
    } finally {
      setMagicBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    try {
      await login(email, password);
      // After login: route client users to the white-label portal so
      // they never see the agency dashboard. Agency users land on `/`.
      const stored = localStorage.getItem('user');
      const parsed = stored && stored !== 'null' && stored !== 'undefined'
        ? JSON.parse(stored)
        : null;
      navigate(userIsClient(parsed) ? '/client' : '/');
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status: number; data?: { errors?: FieldErrors; message?: string } };
      };
      const status = axiosErr?.response?.status;
      if (status === 422) {
        const errors = axiosErr.response?.data?.errors ?? {};
        setFieldErrors(errors);
      } else if (status === 401) {
        toast.error('Nieprawidłowy adres email lub hasło');
      } else {
        toast.error('Błąd logowania. Spróbuj ponownie.');
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="card w-full max-w-md p-8 dark:bg-slate-800 dark:border-slate-700">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-brand-500 dark:bg-brand-600 flex items-center justify-center text-white font-bold">
            A
          </div>
          <div>
            <h1 className="text-xl font-semibold dark:text-white">AIAds 2.0</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Google Ads AI Platform</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Sign in form">
          <div>
            <label htmlFor="login-email" className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Email
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
              className="input mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-slate-400"
              placeholder="twoj@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldErrors.email && (
              <p id="login-email-error" className="mt-1 text-xs text-red-500">{fieldErrors.email[0]}</p>
            )}
          </div>

          <div>
            <label htmlFor="login-password" className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Hasło
            </label>
            <input
              id="login-password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
              aria-invalid={!!fieldErrors.password}
              aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
              className="input mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder-slate-400"
              placeholder="Wpisz hasło..."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors.password && (
              <p id="login-password-error" className="mt-1 text-xs text-red-500">{fieldErrors.password[0]}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full dark:bg-brand-600 dark:hover:bg-brand-700"
          >
            {isLoading ? 'Logowanie...' : 'Zaloguj się'}
          </button>
        </form>

        {/* intg_ — alternate sign-in methods */}
        <div className="mt-4 space-y-2">
          <button
            type="button"
            onClick={handleSsoGoogle}
            disabled={ssoBusy}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium py-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {ssoBusy ? 'Przekierowuję…' : 'Zaloguj przez Google'}
          </button>

          <button
            type="button"
            onClick={handleMagicLink}
            disabled={magicBusy || !email}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium py-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
          >
            {magicBusy ? 'Wysyłam…' : 'Wyślij magic link'}
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 text-center space-y-2">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Nie masz konta?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-medium"
            >
              Załóż agencję
            </button>
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Chcesz przejść kreator krok-po-kroku?{' '}
            <button
              type="button"
              onClick={() => navigate('/onboarding/register')}
              className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-medium"
            >
              Otwórz kreator onboardingu →
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
